import { Factory, Model, Response } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import type { MockServer } from './server.js'
import { createErrorResponse } from './utils.js'
import { safeMathRandom } from '@/utils/number.js'
import { subtractToDate } from '@/utils/date/calculation.js'
import {
  getCurrentTimestamp,
  getDateTimeTimestamp,
  getTodayDateTimeISO,
} from '@/utils/date/getter.js'

/**
 * AI Chat WebSocket Streaming - Backend Implementation Reference
 *
 * WEBSOCKET CHANNEL NAMING:
 * - Channel name format: `{provet_id}.reports_v2.ai_chat.{conversation_id}`
 * - Example: `1234.reports_v2.ai_chat.550e8400-e29b-41d4-a716-446655440000`
 * - The full channel name is returned as `channelId` from the start endpoint
 *
 * STREAMING FLOW:
 * 1. Frontend calls POST /ai_chat/start which returns the full channel name as channelId
 * 2. Backend processes the request asynchronously
 * 3. Backend streams AI response chunks via websocket to the channel
 * 4. Backend sends completion message when done
 *
 * WEBSOCKET MESSAGE FORMAT:
 * {
 *   action: string,  // "reports_v2.ai_chat.chunk" or "reports_v2.ai_chat.complete"
 *   content: string  // HTML-formatted text chunk or empty string for complete
 * }
 *
 * MESSAGE ACTIONS:
 * - "reports_v2.ai_chat.chunk" - Incremental content chunk (add to existing message)
 * - "reports_v2.ai_chat.complete" - Message streaming is complete
 *
 * STREAMING RECOMMENDATIONS:
 * - Send chunks every 50-100ms for smooth streaming effect
 * - Chunk size can vary (1-50 characters per chunk works well)
 * - Always send complete action when finished
 * - Content should be HTML formatted (will be sanitized by DOMPurify on frontend)
 * - Support common HTML tags: p, br, strong, em, ul, ol, li, h1-h6, blockquote, code, pre
 *
 * EXAMPLE STREAMING SEQUENCE:
 * 1. { action: 'reports_v2.ai_chat.chunk', content: 'Hello! ' }
 * 2. { action: 'reports_v2.ai_chat.chunk', content: 'How can ' }
 * 3. { action: 'reports_v2.ai_chat.chunk', content: 'I help you?' }
 * 4. { action: 'reports_v2.ai_chat.complete', content: '' }
 *
 * BACKEND IMPLEMENTATION:
 * See: pcloud/provet/reporting_v2/services/ai_chat.py
 * - AIChatService: Main service handling websocket communication
 * - Channel format defined in get_frontend_channel()
 * - Messages sent via Redis pub/sub to socketly (pcloud_node websocket server)
 */

// TODO: Update these types once the API is updated to return the correct type
type AIChatMessage = {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
}

export type AIChatChannelData = {
  channel_id: string
  messages: AIChatMessage[]
  created_at: string
  updated_at: string
}

export const model = Model as ModelDefinition<AIChatChannelData>

export const factory = Factory.extend<AIChatChannelData>({
  channel_id() {
    return `channel-${Math.floor(safeMathRandom() * 1000000)}`
  },
  messages() {
    return []
  },
  created_at() {
    return getTodayDateTimeISO()
  },
  updated_at() {
    return getTodayDateTimeISO()
  },
})

/**
 * Context data for clinic simulations
 */
const clinicData = {
  'bath-clinic': {
    name: 'Bath Clinic',
    consultations: 245,
    revenue: 32500,
    discountPolicy: 'all-invoices',
    averageInvoice: 132.65,
  },
  'ocean-beach-clinic': {
    name: 'Ocean Beach Clinic',
    consultations: 245,
    revenue: 42800,
    discountPolicy: 'tag-x-only',
    averageInvoice: 174.69,
  },
}

/**
 * Extract conversation context from message history
 */
const getConversationContext = (messages: AIChatMessage[]): string => {
  return messages
    .map((msg) => `${msg.role}: ${msg.content}`)
    .join('\n')
    .toLowerCase()
}

/**
 * Generate a contextual AI response based on conversation history
 */
const generateMockAIResponse = (
  prompt: string,
  messageHistory: AIChatMessage[] = [],
): string => {
  const lowerPrompt = prompt.toLowerCase()
  const context = getConversationContext(messageHistory)

  // Greeting responses
  if (lowerPrompt.includes('hello') || lowerPrompt.includes('hi')) {
    return 'Hello! How can I assist you with your veterinary practice today?'
  }

  if (lowerPrompt.includes('help')) {
    return "I'm here to help! You can ask me questions about patient records, billing, appointments, inventory management, clinic performance, or any other aspect of running your veterinary practice. What would you like to know?"
  }

  // Clinic performance and comparison
  if (
    (lowerPrompt.includes('clinic') || lowerPrompt.includes('practice')) &&
    (lowerPrompt.includes('doing') ||
      lowerPrompt.includes('performance') ||
      lowerPrompt.includes('compare') ||
      lowerPrompt.includes('revenue') ||
      lowerPrompt.includes('consultation'))
  ) {
    return `I've analyzed your clinics' performance. Here's what I found:

**${clinicData['bath-clinic'].name}:**
- Consultations: ${clinicData['bath-clinic'].consultations}
- Revenue: $${clinicData['bath-clinic'].revenue.toLocaleString()}
- Average per consultation: $${clinicData['bath-clinic'].averageInvoice}

**${clinicData['ocean-beach-clinic'].name}:**
- Consultations: ${clinicData['ocean-beach-clinic'].consultations}
- Revenue: $${clinicData['ocean-beach-clinic'].revenue.toLocaleString()}
- Average per consultation: $${clinicData['ocean-beach-clinic'].averageInvoice}

Both clinics have the same number of consultations (${clinicData['bath-clinic'].consultations}), but there's a significant difference in revenue. ${clinicData['ocean-beach-clinic'].name} is generating about 32% more revenue despite seeing the same number of patients.`
  }

  // Why question - explaining the difference
  if (
    context.includes('revenue') &&
    context.includes('consultation') &&
    (lowerPrompt.includes('why') ||
      lowerPrompt.includes('explain') ||
      lowerPrompt.includes('difference') ||
      lowerPrompt.includes('how come'))
  ) {
    return `The revenue difference is due to discount policies:

**${clinicData['bath-clinic'].name}** applies discounts to ALL invoices (current policy: "${clinicData['bath-clinic'].discountPolicy}"), which reduces the total revenue significantly.

**${clinicData['ocean-beach-clinic'].name}** only applies discounts to invoices with tag "X" (current policy: "${clinicData['ocean-beach-clinic'].discountPolicy}"), meaning fewer invoices receive discounts.

This policy difference results in ${clinicData['bath-clinic'].name} having a lower average invoice amount ($${clinicData['bath-clinic'].averageInvoice}) compared to ${clinicData['ocean-beach-clinic'].name} ($${clinicData['ocean-beach-clinic'].averageInvoice}), even though both see the same number of patients.

Would you like me to help standardize these policies?`
  }

  // Action request - changing settings
  if (
    context.includes('discount') &&
    (lowerPrompt.includes('change') ||
      lowerPrompt.includes('update') ||
      lowerPrompt.includes('modify') ||
      lowerPrompt.includes('set') ||
      lowerPrompt.includes('fix') ||
      lowerPrompt.includes('standardize') ||
      lowerPrompt.includes('same') ||
      lowerPrompt.includes('both'))
  ) {
    // Simulate the change
    clinicData['bath-clinic'].discountPolicy = 'tag-x-only'

    return `✓ Done! I've updated the discount policy for ${clinicData['bath-clinic'].name}.

**Changes made:**
- ${clinicData['bath-clinic'].name} discount policy: Changed from "all-invoices" to "tag-x-only"

Both clinics now use the same discount policy (tag X only). This should help ${clinicData['bath-clinic'].name}'s revenue increase to match ${clinicData['ocean-beach-clinic'].name}'s performance in the coming months.

The change will take effect immediately for all new invoices. Would you like me to generate a report to track the impact of this change over the next 30 days?`
  }

  // Follow-up confirmations or thanks
  if (
    (lowerPrompt.includes('thank') ||
      lowerPrompt.includes('great') ||
      lowerPrompt.includes('perfect') ||
      lowerPrompt.includes('good')) &&
    context.includes('done')
  ) {
    return "You're welcome! Is there anything else you'd like me to help you with regarding your clinics or practice management?"
  }

  // Report generation
  if (
    (lowerPrompt.includes('yes') || lowerPrompt.includes('sure')) &&
    context.includes('report')
  ) {
    return `Great! I'll set up a 30-day tracking report comparing ${clinicData['bath-clinic'].name}'s revenue before and after the policy change. You'll receive weekly updates and a full analysis at the end of the period. The report will be available in your Reports dashboard.`
  }

  // Patient-related queries
  if (lowerPrompt.includes('patient')) {
    return 'I can help you with patient-related queries. You can ask about patient histories, medical records, treatment plans, medications, or any other patient information. What specific information are you looking for?'
  }

  // Billing and invoicing
  if (lowerPrompt.includes('invoice') || lowerPrompt.includes('billing')) {
    return 'For billing and invoicing questions, I can help you understand payment statuses, generate financial reports, manage outstanding balances, or assist with invoice creation. What would you like to know?'
  }

  // Appointments and scheduling
  if (lowerPrompt.includes('appointment') || lowerPrompt.includes('schedule')) {
    return 'I can assist with scheduling appointments, managing your calendar, checking availability, and helping with appointment-related queries. How can I help with scheduling?'
  }

  // Inventory management
  if (lowerPrompt.includes('inventory') || lowerPrompt.includes('stock')) {
    return 'For inventory management, I can help you track stock levels, manage suppliers, check item availability, or assist with ordering. What inventory information do you need?'
  }

  // Default response
  return `I understand you're asking about "${prompt}". As an AI assistant for veterinary practice management, I can help you with:

- Clinic performance analysis and comparisons
- Patient records and medical histories
- Billing and financial reports
- Appointments and scheduling
- Inventory management
- Settings and configuration changes

Could you provide more specific details about what you need help with?`
}

export function routes(server: MockServer) {
  /**
   * POST /{provet_id}/api/reports_v2/ai_chat/start
   *
   * Starts a new chat session and returns a full channel name as channelId.
   * Channel format: `{provet_id}.reports_v2.ai_chat.{conversation_id}`
   *
   * WEBSOCKET BEHAVIOR:
   * After this endpoint returns, backend asynchronously:
   * 1. Generates AI response for the prompt
   * 2. Streams response via websocket channel (the full channelId)
   * 3. Sends chunks with action: 'reports_v2.ai_chat.chunk'
   * 4. Sends completion with action: 'reports_v2.ai_chat.complete'
   */
  server.post('/reports_v2/ai_chat/start', function (schema, request) {
    const body = JSON.parse(request.requestBody)
    const prompt = body.data?.attributes?.prompt

    if (!prompt || typeof prompt !== 'string') {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'prompt is required and must be a string' }],
      })
    }

    // Create a new chat channel
    const nowTimestamp = getCurrentTimestamp().toString()
    const channel = server.create('aiChat', {
      messages: [
        {
          role: 'user',
          content: prompt,
          timestamp: nowTimestamp,
        },
      ],
      updated_at: nowTimestamp,
    })

    const channelId = channel.channel_id

    // Simulate AI processing delay
    setTimeout(
      () => {
        // Re-fetch the channel to ensure we have the latest data
        const currentChannel = schema.findBy('aiChat', {
          channel_id: channelId,
        })

        const nowTimestamp = getCurrentTimestamp().toString()
        if (currentChannel) {
          const assistantResponse = generateMockAIResponse(
            prompt,
            currentChannel.messages,
          )
          const updatedMessages = [
            ...currentChannel.messages,
            {
              role: 'assistant',
              content: assistantResponse,
              timestamp: nowTimestamp,
            },
          ]
          currentChannel.update({
            messages: updatedMessages,
            updated_at: nowTimestamp,
          })
        }
      },
      800 + safeMathRandom() * 1200,
    ) // Random delay 800-2000ms

    return new Response(
      200,
      {},
      {
        data: {
          attributes: {
            channel_id: channel.channel_id,
          },
        },
      },
    )
  })

  /**
   * POST /{provet_id}/api/reports_v2/ai_chat/reply
   *
   * Sends a reply in an existing chat session.
   *
   * WEBSOCKET BEHAVIOR:
   * After this endpoint returns, backend asynchronously:
   * 1. Generates AI response for the prompt in context of conversation history
   * 2. Streams response via websocket channel (the channelId from start)
   * 3. Sends chunks with action: 'reports_v2.ai_chat.chunk'
   * 4. Sends completion with action: 'reports_v2.ai_chat.complete'
   */
  server.post('/reports_v2/ai_chat/reply', function (schema, request) {
    const body = JSON.parse(request.requestBody)
    const prompt = body.data?.attributes?.prompt
    const channelId = body.data?.attributes?.channel_id

    if (!prompt || typeof prompt !== 'string') {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'prompt is required and must be a string' }],
      })
    }

    if (!channelId || typeof channelId !== 'string') {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'channelId is required and must be a string' }],
      })
    }

    // Find the channel
    const channel = schema.findBy('aiChat', {
      channel_id: channelId,
    })

    if (!channel) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: 'Chat channel not found' }],
      })
    }

    // Add user message
    const nowTimestamp = getCurrentTimestamp().toString()
    const userMessages = [
      ...channel.messages,
      {
        role: 'user',
        content: prompt,
        timestamp: nowTimestamp,
      },
    ]
    channel.update({
      messages: userMessages,
      updated_at: nowTimestamp,
    })

    // Simulate AI processing delay
    setTimeout(
      () => {
        // Re-fetch the channel to ensure we have the latest data
        const currentChannel = schema.findBy('aiChat', {
          channel_id: channelId,
        })
        if (currentChannel) {
          const assistantResponse = generateMockAIResponse(
            prompt,
            currentChannel.messages,
          )
          const nowTimestamp = getCurrentTimestamp().toString()
          const updatedMessages = [
            ...currentChannel.messages,
            {
              role: 'assistant',
              content: assistantResponse,
              timestamp: nowTimestamp,
            },
          ]
          currentChannel.update({
            messages: updatedMessages,
            updated_at: nowTimestamp,
          })
        }
      },
      800 + safeMathRandom() * 1200,
    ) // Random delay 800-2000ms

    // Return 204 No Content
    return new Response(204)
  })

  /**
   * DELETE /{provet_id}/api/reports_v2/ai_chat/close
   *
   * Closes and cleans up a chat session.
   * Backend should stop any ongoing streaming and clean up resources.
   */
  server.delete('/reports_v2/ai_chat/close', function (schema, request) {
    const channelId = request.queryParams.channel_id

    if (!channelId || typeof channelId !== 'string') {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'channelId is required and must be a string' }],
      })
    }

    // Find and destroy the channel
    const channel = schema.findBy('aiChat', {
      channel_id: channelId,
    })

    if (!channel) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: 'Chat channel not found' }],
      })
    }

    channel.destroy()

    // Return 205 Reset Content
    return new Response(205)
  })

  /**
   * GET /{provet_id}/api/reports_v2/ai_chat/poll
   *
   * NOTE: This polling endpoint is NO LONGER USED by the frontend.
   * The frontend now uses websocket streaming for real-time message updates.
   * This endpoint is kept for backwards compatibility but may be removed in the future.
   *
   * @deprecated Use websocket streaming instead
   */
  server.get('/reports_v2/ai_chat/poll', function (schema, request) {
    const channelId = request.queryParams.channelId

    if (!channelId || typeof channelId !== 'string') {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'channelId query parameter is required' }],
      })
    }

    // Find the channel
    const channel = schema.findBy('aiChat', { channel_id: channelId })

    if (!channel) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: 'Chat channel not found' }],
      })
    }

    return new Response(
      200,
      {},
      {
        data: {
          attributes: {
            channel_id: channel.channel_id,
            messages: channel.messages,
            updated_at: channel.updated_at,
          },
        },
      },
    )
  })
}

export function seeds(server: MockServer) {
  // Create a sample channel for testing
  const twoMinutesAgo = getDateTimeTimestamp(
    subtractToDate(getTodayDateTimeISO(), { minutes: 2 }),
  ).toString()
  const oneMinuteAgo = getDateTimeTimestamp(
    subtractToDate(getTodayDateTimeISO(), { minutes: 1 }),
  ).toString()

  server.create('aiChat', {
    channel_id: 'channel-demo-123',
    messages: [
      {
        role: 'user',
        content: 'Hello, can you help me?',
        timestamp: twoMinutesAgo,
      },
      {
        role: 'assistant',
        content:
          'Hello! Of course, I can help you with your veterinary practice. What would you like to know?',
        timestamp: oneMinuteAgo,
      },
    ],
    created_at: twoMinutesAgo,
    updated_at: oneMinuteAgo,
  })
}
